import { useEffect, useMemo, useState } from 'react';

import { Divider, Drawer, Loader, Menu, Text } from '@mantine/core';

import ActivityFeedIcon from '@/assets/icons/ActivityFeedIcon';
import BackIcon from '@/assets/icons/BackIcon';
import FilterMenuIcon from '@/assets/icons/FilterMenuIcon';

import styles from './ActivityFeed.module.scss';
import { ActivityItem } from './components/ActivityItem';
import { FilterMenu } from './components/FilterMenu';
import { useActivityFeed } from './hooks/useActivityFeed';
import { groupActivitiesByDate } from './utils';

export const ACTIVITY_FEED_WIDTH = 300;

export function ActivityFeed({
  patientId,
  isPatientDataLoading
}: Readonly<{ patientId?: string; isPatientDataLoading: boolean }>) {
  const {
    activityItems,
    isLoading,
    isFetchingMore,
    isError,
    isOpen,
    toggleDrawer,
    lastElementRef,
    filterOptions,
    updateFilterOptions,
    isFilterLoading,
    isInitialLoad
  } = useActivityFeed(patientId ?? '');
  const [selectedOptions, setSelectedOptions] = useState<string[]>(filterOptions);

  useEffect(() => {
    setSelectedOptions(filterOptions);
  }, [filterOptions]);

  const groupedActivities = useMemo(() => groupActivitiesByDate(activityItems), [activityItems]);

  const renderActivityItems = () => {
    if (isError) {
      return (
        <div className={styles.errorContainer}>
          <Text color="red">Error loading activity data</Text>
        </div>
      );
    }

    if ((isLoading || isInitialLoad || isFilterLoading) && !activityItems) {
      return (
        <div className={styles.loadingContainer}>
          <Loader size="md" />
        </div>
      );
    }

    if (!activityItems || activityItems.length === 0) {
      return (
        <div className={styles.emptyContainer}>
          <Text>No activity found</Text>
        </div>
      );
    }

    return (
      <div className={styles.activityListContainer}>
        {Object.entries(groupedActivities).map(([date, activities], dateIndex, datesArray) => (
          <div key={date} className={styles.activityDateGroup}>
            <Divider className={styles.dateLabel} label={date} />
            {activities.map((activity, activityIndex) => {
              const isLastActivity =
                dateIndex === datesArray.length - 1 && activityIndex === activities.length - 1;

              // Apply ref only to the last element
              return (
                <div
                  className={styles.activityItemContainer}
                  key={activity.eventId}
                  ref={isLastActivity ? lastElementRef : undefined}>
                  <ActivityItem item={activity} />
                </div>
              );
            })}
          </div>
        ))}

        {isFetchingMore && (
          <div className={styles.bottomLoader}>
            <Loader size="sm" />
          </div>
        )}
      </div>
    );
  };

  const renderDrawerHeader = () => (
    <div className={styles.activityHeader}>
      <div className={styles.activityTitle}>
        <span className={styles.closeIcon} onClick={toggleDrawer}>
          <BackIcon />
        </span>
        Activity
      </div>
      <Menu
        position="bottom-end"
        shadow="md"
        onClose={() => {
          if (
            selectedOptions.length === filterOptions.length &&
            selectedOptions.every((opt) => filterOptions.includes(opt))
          ) {
            return;
          }
          updateFilterOptions(selectedOptions);
        }}>
        <Menu.Target>
          <div className={styles.filtersToggle} data-testid="filters-toggle">
            {isFilterLoading ? <Loader size="xs" /> : <FilterMenuIcon />}
          </div>
        </Menu.Target>
        <Menu.Dropdown>
          <FilterMenu selectedOptions={selectedOptions} setSelectedOptions={setSelectedOptions} />
          {isFilterLoading && (
            <div className={styles.menuLoaderOverlay}>
              <Loader size="sm" />
            </div>
          )}
        </Menu.Dropdown>
      </Menu>
    </div>
  );

  return (
    <>
      {!isOpen && (
        <div
          onClick={toggleDrawer}
          className={styles.drawerToggleBtn}
          data-testid="activity-feed-toggle">
          {isPatientDataLoading ? <Loader size="sm" color="white" /> : <ActivityFeedIcon />}
        </div>
      )}

      <Drawer
        opened={isOpen}
        onClose={toggleDrawer}
        withCloseButton={false}
        position="right"
        size={ACTIVITY_FEED_WIDTH}
        overlayProps={{ backgroundOpacity: 0 }}
        classNames={{
          root: styles.feedDrawer
        }}
        title={renderDrawerHeader()}
        data-testid="activity-feed-drawer">
        <div className={styles.drawerContentWrapper}>
          {(isLoading || isFilterLoading) && activityItems.length === 0 ? (
            <div className={styles.fullDrawerLoader}>
              <Loader size="md" />
            </div>
          ) : (
            <div className={styles.activityContent}>{renderActivityItems()}</div>
          )}
        </div>
      </Drawer>
    </>
  );
}
